import React from "react";
import autoBind from "react-autobind";
import LeadDrawerActions from "~/actions/lead_drawer_actions";
import DateTimePicker from "~/components/forms/DateTimePicker";
import TimeSelect from "~/components/forms/dropdowns/TimeSelect";
import ActivityEditor from "~/components/forms/HtmlEditors/ActivityEditor";
import NoteTemplateSelect from "~/components/forms/Templates/NoteTemplateSelect";
import InsertDocumentButton from "~/components/insert_document_button";
import UnsupportedVariablesWarning from "~/components/modals/UnsupportedVariablesWarning";
import { captureEvent } from "~/helpers/capture_posthog_event";
import LeadHelpers from "~/helpers/lead_helpers";
import { isAdminOrStaff } from "~/helpers/userRoleHelper";
import dayjs from "~/lib/dayjs";
import { normalizeUnsupportedVariablesError } from "~/lib/unsupportedVariables";
import FormFooter from "./common/form_footer";

const CurrentDate = dayjs().startOf("day").toDate();

class LogAppointmentForm extends React.Component {
  constructor(props) {
    super(props);

    const { lead } = this.props;

    this.state = {
      occurred_at_date: dayjs().local(),
      occurred_at_time: null,
      body: "",
      status: lead.status,
      internal: GlobalContainer.product() === "retention",
      rating: props.lead.rating,
      errors: {},
      templateBody: null,
      linksToInsert: [],
      variablesWarning: null,
    };

    autoBind(this);
  }

  applyTemplate(template) {
    if (template) {
      this.setState({
        body: template.body || "",
        templateBody: template.body || "",
        errors: {},
      });
    } else {
      this.setState({
        body: "",
        templateBody: null,
        errors: {},
      });
    }
  }

  onChangeDate(m) {
    this.setState(({ errors }) => {
      if (!m) {
        return { occurred_at_date: null };
      }

      const { occurred_at_date: _dateError, ...otherErrors } = errors;
      return { occurred_at_date: m, errors: otherErrors };
    });
  }

  onFormSubmit = (e, saveAnyway = false) => {
    e.preventDefault();
    const { body, occurred_at_date } = this.state;
    const { lead } = this.props;

    this.setState({ sending: true });

    const errors = this.validate();

    if (_lodash.size(errors) === 0) {
      const date = dayjs(occurred_at_date)
        .set("hour", 0)
        .set("minutes", 0)
        .set("second", 0);

      const footer = this.footer.serialize();
      const { status, rating, task, notify, internal } = footer;

      const data = {
        status,
        rating,
        task,
        event: "appointment",
        save_anyway: saveAnyway,
        activity: {
          body,
          occurred_at: date,
          internal,
        },
      };

      LeadDrawerActions.createLeadActivity(lead, data, {
        onUnsupportedVariables: (warning) => {
          this.footer?.resetSending?.();
          this.setState({
            sending: false,
            variablesWarning: normalizeUnsupportedVariablesError(warning),
          });
        },
        onDone: () => {
          const product = GlobalContainer.product();

          captureEvent(`${product}_appointment_logged`, {
            contact_id: lead.id,
            status,
            has_notes: !!body,
            has_followup_task: !!task,
          });
          this.footer?.resetSending?.();
          this.setState({ sending: false, variablesWarning: null });
        },
        onFail: () => {
          this.footer?.resetSending?.();
          this.setState({ sending: false, variablesWarning: null });
          GlobalContainer.notify(
            "Failed to log appointment. Please try again.",
            "error",
          );
        },
      });
    } else {
      this.setState({ sending: false, errors });
    }
  };

  validate() {
    const { occurred_at_date } = this.state;
    const errors = {};

    if (!occurred_at_date) {
      errors.occurred_at_date = "Can't be empty";
    }

    return this.footer.validate(errors);
  }

  render() {
    const { lead } = this.props;
    const {
      errors,
      occurred_at_date,
      status,
      internal,
      rating,
      occurred_at_time,
      linksToInsert,
      sending,
      variablesWarning,
    } = this.state;
    const userTimezone = LeadHelpers.tzAbbr(dayjs.tz.guess());

    return (
      <form onSubmit={this.onFormSubmit}>
        <label htmlFor="appt_datetime" className="d-block">
          Appt Date and Time
        </label>
        <div className="row mb10">
          <div className="col-sm-6 col-xl-4 pl0 pr0-xs-down mb5">
            <DateTimePicker
              id="appt_datetime"
              placeholder="Select Date"
              format="LL"
              pickerType="date"
              maxDate={CurrentDate}
              isInvalid={errors.occurred_at_date}
              value={occurred_at_date ? occurred_at_date.format("LL") : ""}
              onChange={this.onChangeDate}
            />
          </div>

          <div className="col-sm-6 col-xl-4 pr0 pl0-xs-down mb5">
            <TimeSelect
              name="status"
              placeholder={`Select Time (${userTimezone})`}
              value={occurred_at_time}
              startHour="6"
              endHour="20"
              className={errors.occurred_at_time ? "has-error" : ""}
              onChange={(opt) => {
                this.setState({ occurred_at_time: opt ? opt.value : "" });
              }}
            />
          </div>
        </div>

        <div className="form-group mb15">
          <label htmlFor="appt_description" className="d-block">
            Note
          </label>
          <ActivityEditor
            className={errors.body ? "tiny-mce has-error" : ""}
            onChange={(html) => {
              this.setState({ body: html });
            }}
            placeholder="Enter Text"
            activityType="log_appointment"
            leadId={this.props.lead.id}
            spellCheck
            enableVariables={isAdminOrStaff()}
            linksToInsert={linksToInsert}
            clearLinksToInsert={() => {
              this.setState({ linksToInsert: [] });
            }}
            replaceValue={this.state.templateBody}
            clearReplaceValue={() => this.setState({ templateBody: null })}
          />
        </div>

        <div className="form-row">
          <div className="form-group col-lg-4">
            <InsertDocumentButton
              setLinksToInsert={(linksToInsert) =>
                this.setState({ linksToInsert })
              }
            />
          </div>
          <div className="form-group col-lg-4 ml-auto">
            <NoteTemplateSelect
              applyTemplate={this.applyTemplate}
              body={this.state.body}
            />
          </div>
        </div>

        <div className="mt15 mb15">
          <FormFooter
            ref={(el) => (this.footer = el)}
            activityType="log_appointment"
            status={status}
            internal={internal}
            rating={rating}
            lead={lead}
            onSubmit={this.onFormSubmit}
            submitButtonText="Log Appointment"
            sending={sending}
            showRatingSelect={GlobalContainer.product() !== "retention"}
          />
        </div>
        <UnsupportedVariablesWarning
          show={!!variablesWarning}
          warning={variablesWarning}
          saving={sending}
          onReview={() => this.setState({ variablesWarning: null })}
          onSaveAnyway={() =>
            this.setState({ variablesWarning: null }, () =>
              this.onFormSubmit({ preventDefault: () => false }, true),
            )
          }
        />
      </form>
    );
  }
}

export default LogAppointmentForm;
